import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FeedbackService } from '../../_services/api/feedback.service';
import { NotificationService } from '../../_services/app/notification.service';
import { 
  FeedbackRequest, 
  FeedbackResponse, 
  AgentFeedbackRequest,
  FeedbackStatus 
} from '../../_models/feedback.model';
import { ApiResponse } from '../../_models/api-response.model';
import { RatingStarsComponent } from '../../_shared_components/rating-stars/rating-stars.component';

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RatingStarsComponent],
  templateUrl: './feedback-form.component.html',
  styleUrls: ['./feedback-form.component.scss']
})
export class FeedbackFormComponent implements OnInit, OnDestroy {
  feedbackForm!: FormGroup;
  reservationId?: number;
  isLoaded = false;
  isLoading = false;
  isSubmitting = false;
  errorMessage = '';
  feedbackData?: FeedbackResponse;
  maxUpdates = 2;
  
  ratingLabels: { [key: number]: string } = {
    1: 'Très insatisfait',
    2: 'Insatisfait',
    3: 'Neutre',
    4: 'Satisfait',
    5: 'Très satisfait'
  };
  
  frenchTranslations = {
    title: 'Évaluation du Service',
    subtitle: 'Merci de partager votre expérience avec Farco-Security',
    overallService: 'Service Global',
    serviceQuality: 'Qualité du Service',
    punctuality: 'Ponctualité',
    communication: 'Communication',
    commentPlaceholder: 'Partagez votre expérience globale...',
    agentFeedback: 'Évaluation des Agents',
    agentTitle: 'Agent',
    professionalism: 'Professionalisme',
    attitude: 'Attitude',
    efficiency: 'Efficacité',
    agentCommentPlaceholder: 'Commentaire pour cet agent...',
    submitButton: 'Soumettre l\'évaluation',
    updateButton: 'Mettre à jour l\'évaluation',
    submitting: 'Envoi en cours...',
    updating: 'Mise à jour en cours...',
    successMessage: 'Merci pour votre évaluation !',
    maxUpdatesReached: 'Vous avez atteint le nombre maximum de mises à jour (2)',
    loadingFeedback: 'Chargement de l\'évaluation...',
    invalidToken: 'Lien d\'évaluation invalide ou expiré',
    requiredField: 'Ce champ est requis',
    requiredRating: 'Veuillez attribuer une note',
    maxLength: 'Maximum 300 caractères',
    charactersRemaining: 'caractères restants'
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private feedbackService: FeedbackService,
    private notification: NotificationService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadFromRoute();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFromRoute(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
       const id = params.get('reservationId');
        if (id) {
          this.reservationId = Number(id);
          this.loadFeedback();
        } else {
          this.errorMessage = this.frenchTranslations.invalidToken;
        }
    });
  }

  private initializeForm(): void {
    this.feedbackForm = this.fb.group({
      overallRating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      serviceQualityRating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      punctualityRating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      communicationRating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      comment: ['', [Validators.maxLength(300)]],
      agentFeedbacks: this.fb.array([])
    });
  }

  private loadFeedback(): void {
    if (!this.reservationId) return;
    
    this.isLoading = true;
    this.errorMessage = '';
    
    this.feedbackService.getFeedback(this.reservationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ 
        next: (response: ApiResponse<FeedbackResponse>) => {
          if (response.success && response.data) {
            this.feedbackData = response.data;
            this.populateForm(response.data);
            this.checkUpdateLimit();
          } else {
            this.isLoaded = true;
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading feedback:', error);
          if (error.status === 409 || error.status === 403) {
            this.router.navigate(['/member/reservations/details', this.reservationId]);
            return;
          }
          //this.errorMessage = error.error?.message || 'Erreur de chargement';
          this.populateForm(null);
          this.isLoading = false;
        }
      });
  }

  private populateForm(data: FeedbackResponse | null ): void {
    this.feedbackForm.patchValue({
      overallRating: data!.overallRating ||  0,
      serviceQualityRating: data!.serviceQualityRating || 0,
      punctualityRating: data!.punctualityRating || 0,
      communicationRating: data!.communicationRating || 0,
      comment: data!.comment || ''
    });

    this.agentFeedbacks.clear();
    if(data){
      data.agentFeedbacks.forEach(agentFeedback => {
        this.addAgentFeedback(agentFeedback);
      });
    }
   
    
    this.isLoaded = true;
  }

  private addAgentFeedback(agentFeedback?: AgentFeedbackRequest & { agentName?: string, profileImage?: string }): void {
    const agentForm = this.fb.group({
      agentId: [agentFeedback?.agentId || 0, Validators.required],
      agentName: [agentFeedback?.agentName || '', Validators.required],
      profileImage: [agentFeedback?.profileImage || ''],
      professionalismRating: [agentFeedback?.professionalismRating || 0, 
        [Validators.required, Validators.min(1), Validators.max(5)]],
      attitudeRating: [agentFeedback?.attitudeRating || 0, 
        [Validators.required, Validators.min(1), Validators.max(5)]],
      efficiencyRating: [agentFeedback?.efficiencyRating || 0, 
        [Validators.required, Validators.min(1), Validators.max(5)]],
      comment: [agentFeedback?.comment || '', Validators.maxLength(300)]
    });
    
    this.agentFeedbacks.push(agentForm);
  }

  private checkUpdateLimit(): void {
    if (this.feedbackData && this.feedbackData.updateCount >= this.maxUpdates) {
      this.feedbackForm.disable();
      this.errorMessage = this.frenchTranslations.maxUpdatesReached;
    }
  }

  onSubmit(): void {
    if (this.feedbackForm.invalid || this.isSubmitting) {
      this.markFormGroupTouched(this.feedbackForm);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const formValue = this.feedbackForm.value;
    const feedbackRequest: FeedbackRequest = {
      overallRating: formValue.overallRating,
      serviceQualityRating: formValue.serviceQualityRating,
      punctualityRating: formValue.punctualityRating,
      communicationRating: formValue.communicationRating,
      comment: formValue.comment || undefined,
      agentFeedbacks: formValue.agentFeedbacks.map((af: any) => ({
        agentId: af.agentId,
        professionalismRating: af.professionalismRating,
        attitudeRating: af.attitudeRating,
        efficiencyRating: af.efficiencyRating,
        comment: af.comment || undefined
      }))
    };

    this.feedbackService.submitFeedback(this.reservationId!, feedbackRequest).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: ApiResponse<FeedbackResponse>) => {
        this.isSubmitting = false;
        this.notification.showSuccess(this.frenchTranslations.successMessage);
        this.router.navigate(['/member/reservations/details', this.reservationId]);
        /*
        this.feedbackData = response.data;
        this.checkUpdateLimit();
        */
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error.error?.message || 'Erreur lors de la soumission';
        console.error('Feedback submission error:', error);
      }
    });
  }

  get agentFeedbacks(): FormArray {
    return this.feedbackForm.get('agentFeedbacks') as FormArray;
  }

  get overallRating(): number {
    return this.feedbackForm.get('overallRating')?.value || 0;
  }

  get serviceQualityRating(): number {
    return this.feedbackForm.get('serviceQualityRating')?.value || 0;
  }

  get punctualityRating(): number {
    return this.feedbackForm.get('punctualityRating')?.value || 0;
  }

  get communicationRating(): number {
    return this.feedbackForm.get('communicationRating')?.value || 0;
  }

  getAgentRating(index: number, field: string): number {
    const agentForm = this.agentFeedbacks.at(index);
    return agentForm.get(field)?.value || 0;
  }

  getAgentControl(index: number, field: string): AbstractControl {
    const agentForm = this.agentFeedbacks.at(index);
    return agentForm.get(field)!;
  }

  getRatingLabel(rating: number): string {
    return this.ratingLabels[rating] || 'Non évalué';
  }

  getAgentFormGroup(index: number): FormGroup {
    const agentControl = this.agentFeedbacks.at(index);
    if (agentControl instanceof FormGroup) {
      return agentControl;
    }
    throw new Error(`Agent control at index ${index} is not a FormGroup`);
  }

  get canUpdate(): boolean {
    return !this.feedbackData || this.feedbackData.updateCount < this.maxUpdates;
  }

  get submitButtonText(): string {
    if (this.isSubmitting) {
      return this.feedbackData ? this.frenchTranslations.updating : this.frenchTranslations.submitting;
    }
    return this.feedbackData ? this.frenchTranslations.updateButton : this.frenchTranslations.submitButton;
  }

  get remainingUpdates(): number {
    return this.feedbackData ? this.maxUpdates - this.feedbackData.updateCount : this.maxUpdates;
  }

  getAgentName(index: number): string {
    const agentForm = this.agentFeedbacks.at(index);
    return agentForm.get('agentName')?.value || `Agent ${index + 1}`;
  }

  getAgentProfileImage(index: number): string {
    const agentForm = this.agentFeedbacks.at(index);
    return agentForm.get('profileImage')?.value || './assets/images/icons/user-default-icon.png';
  }

  getCommentLength(controlName: string): number {
    const control = this.feedbackForm.get(controlName);
    return control?.value?.length || 0;
  }

  getAgentCommentLength(index: number): number {
    const agentForm = this.agentFeedbacks.at(index);
    return agentForm.get('comment')?.value?.length || 0;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          }
        });
      }
    });
  }

  // validations
  hasError(controlName: string, errorName: string): boolean {
    const control = this.feedbackForm.get(controlName);
    return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
  }

  hasAgentError(index: number, controlName: string, errorName: string): boolean {
    const agentForm = this.getAgentFormGroup(index);
    const control = agentForm.get(controlName);
    return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
  }

  getErrorMessage(controlName: string, errorName: string): string {
    const messages: { [key: string]: string } = {
      'required': this.frenchTranslations.requiredRating,
      'min': 'Note minimale : 1',
      'max': 'Note maximale : 5',
      'maxlength': this.frenchTranslations.maxLength
    };
    return messages[errorName] || 'Erreur de validation';
  }

  goToList(): void {
    this.router.navigate(['/member/reservations/details', this.reservationId]);
  }
}